import PropTypes from 'prop-types';
import { Dialog, DialogContent, DialogTitle, Skeleton, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/Iconify';
import {
  useManagedBasePolicySettings,
  useUpdateManagedBasePolicy,
} from 'src/hooks/environmentWdacPolicy';
import { pagesText } from 'src/constants/pagesText';
import EditManagedBasePolicyForm from './EditManagedBasePolicyForm';

// Saving is also what brings a base policy under management, so the same dialog serves editing one
// the portal already manages and starring a detected one - and cancelling out of the second leaves
// the policy unmanaged.
const EditManagedBasePolicyModal = ({ basePolicy, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const settingsQuery = useManagedBasePolicySettings({ envPolicyId: basePolicy.id });
  const { mutateAsync: updateManagedBasePolicy, isLoading } = useUpdateManagedBasePolicy();

  const settings = settingsQuery.data;
  const isAlreadyManaged = !!settings?.isPortalManaged;

  const handleSubmit = async payload => {
    try {
      await updateManagedBasePolicy({ envPolicyId: basePolicy.id, payload });
      enqueueSnackbar(
        isAlreadyManaged
          ? 'Base policy updated.'
          : `${basePolicy.policyName} is now a managed base policy.`,
        { variant: 'success', autoHideDuration: 3000 },
      );
      onClose();
    } catch (error) {
      // The api layer rejects with the backend's message as a plain string, and the rules behind
      // managing a base policy are worth showing verbatim.
      enqueueSnackbar(typeof error === 'string' && error ? error : pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: 'background.dialog' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify icon="mdi:shield-edit-outline" width={22} height={22} color="#87C8E0" />
        {isAlreadyManaged ? 'Edit Managed Base Policy' : 'Manage Base Policy'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={0.25} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#F3F4F6', fontWeight: 600 }}>
            {basePolicy.policyName}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
            {basePolicy.policyId}
          </Typography>
        </Stack>

        {settingsQuery.isLoading ? (
          <Stack spacing={1}>
            {[0, 1, 2].map(index => (
              <Skeleton key={index} variant="rounded" height={40} sx={{ bgcolor: '#454545' }} />
            ))}
          </Stack>
        ) : settingsQuery.isError || !settings ? (
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {typeof settingsQuery.error === 'string' && settingsQuery.error
              ? settingsQuery.error
              : pagesText.somethingWentWrong}
          </Typography>
        ) : (
          <EditManagedBasePolicyForm
            settings={settings}
            loading={isLoading}
            onCancel={onClose}
            onSubmit={handleSubmit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

EditManagedBasePolicyModal.propTypes = {
  basePolicy: PropTypes.shape({
    id: PropTypes.number,
    policyName: PropTypes.string,
    policyId: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditManagedBasePolicyModal;
